'use client'

import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { cj } from '@snowball-tech/fractal/dist/src/styles/helpers'
import { Permanent_Marker as HandwrittentFont } from 'next/font/google'

import isEmpty from 'lodash/fp/isEmpty'

import { Apps } from '../enums'
import { getNewsletterUrl } from '../helpers/navigation'
import { getCleanName } from '../helpers/newsletters'
import useAppName from '../hooks/useAppName'
import useNewsletter from '../hooks/useNewsletter'
import useUrls from '../hooks/useUrls'
import { Newsletter } from '../types'
import Link from './Link'

const font = HandwrittentFont({
  display: 'swap',
  subsets: ['latin'],
  weight: '400',
})

type Props = {
  newsletter: Newsletter
  illustrationUrl?: string
}
export default function NewsletterBanner({
  illustrationUrl,
  newsletter,
}: Props) {
  const appName = useAppName()

  const assetsURLs = useUrls()

  const { name: title, subtitle } = useNewsletter(newsletter, {
    fullQualifiedUrl: appName !== Apps.Media,
  })

  const href =
    appName === Apps.Creators
      ? getNewsletterUrl(newsletter)
      : `/${getNewsletterUrl(newsletter, { fullQualified: false })}`

  return (
    <Link
      className="relative mb-3 w-full @container no-underline"
      href={href}
      target={appName === Apps.Creators ? '_blank' : undefined}
    >
      <div className="h-[200px] max-h-[200px] w-full bg-white bg-repeat [background-image:linear-gradient(to_bottom,var(--color-base-grey-90)_1px,transparent_1px),linear-gradient(to_right,var(--color-base-grey-90)_1px,transparent_1px)] [background-position-x:calc(var(--size-spacing-3)*-1)] [background-size:var(--size-spacing-6)_var(--size-spacing-6)]">
        <div
          className="flex h-full flex-col gap-2 bg-no-repeat [background-position:bottom_25%_left_theme(spacing.2)] [background-size:340px]"
          style={
            isEmpty(title)
              ? {}
              : {
                  backgroundImage: `url(${assetsURLs.assets}/images/ink-stain.svg)`,
                }
          }
        >
          <div className="flex h-full flex-row gap-2">
            <div className="relative flex size-full min-w-[358px] flex-col">
              <div className="ml-4 mt-3 max-w-15 h-[22px]"></div>

              <div
                className={cj(
                  'flex w-full flex-1 items-center pl-10',
                  font.className,
                  'uppercase text-secondary [font-size:theme(spacing.5)] no-underline',
                )}
              >
                {title === 'Snowball' ? 'Newsletter' : title}
              </div>

              {!isEmpty(subtitle) && (
                <Typography
                  className="p-1 text-right no-underline"
                  variant="body-1-bold"
                >
                  {subtitle}
                </Typography>
              )}
            </div>

            {!isEmpty(illustrationUrl) && (
              <div className="relative mx-2 hidden size-full max-h-[calc(100%-theme(spacing.4))] max-w-[calc(200px+theme(spacing.4))] place-self-center @[550px]:block">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  alt={getCleanName(title)}
                  className="object-contain absolute size-full inset-0 text-transparent"
                  src={illustrationUrl}
                  title={getCleanName(title)}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className="absolute -bottom-3 left-0 h-3 w-full bg-repeat-x"
        style={{
          backgroundImage: `url(${assetsURLs.assets}/images/paper-bottom.svg)`,
        }}
      />
    </Link>
  )
}
